import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Field, TextInput } from "@/components/common/Forms";
import { Loader } from "@/components/common/Loader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";

const ROLES = { admin: "Admin", superadmin: "Super Admin" };

function AddStaff({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "admin" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", f);
      toast.success("Utente creato. Comunica tu la password iniziale.");
      setF({ name: "", email: "", password: "", role: "admin" });
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory-card rounded-xl" data-testid="add-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Nuovo membro dello staff</DialogTitle>
          <DialogDescription>Potrà cambiare la password dal proprio account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome e cognome"><TextInput data-testid="staff-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label="Email"><TextInput data-testid="staff-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
          <Field label="Password iniziale" hint="Almeno 8 caratteri"><TextInput data-testid="staff-password-input" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} /></Field>
          <Field label="Ruolo">
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="staff-role-select" className="bg-ivory-deep rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <button data-testid="staff-create-btn" className="btn-gold w-full">Crea utente</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function StaffManager() {
  const { user } = useAuth();
  const { data, loading, reload } = useFetch("/admin/staff");
  const [open, setOpen] = useState(false);
  const patch = async (id, body, msg = "Aggiornato") => {
    try {
      await api.patch(`/admin/staff/${id}`, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resetPw = (u) => {
    const pw = window.prompt(`Nuova password per ${u.name} (almeno 8 caratteri)`);
    if (pw && pw.length >= 8) patch(u.id, { password: pw }, "Password reimpostata");
    else if (pw) toast.error("La password deve avere almeno 8 caratteri");
  };
  if (loading && !data) return <Loader />;
  return (
    <section className="lux-card p-6 sm:p-8" data-testid="staff-manager">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-2xl font-normal">Staff</h2>
        <button data-testid="open-add-staff-btn" onClick={() => setOpen(true)} className="btn-ghost h-10"><Plus className="w-4 h-4" /> Aggiungi</button>
      </div>
      <div className="divide-y divide-warm/30">
        {(data || []).map((u) => (
          <div key={u.id} data-testid={`staff-row-${u.id}`} className="py-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[180px]">
              <p className={`text-sm ${u.active ? "" : "text-warm-text line-through"}`}>{u.name} {u.id === user.id && <span className="text-xs text-warm-text">(tu)</span>}</p>
              <p className="text-xs text-warm-text">{u.email}</p>
            </div>
            <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })} disabled={u.id === user.id}>
              <SelectTrigger data-testid={`staff-role-${u.id}`} className="w-40 bg-ivory-deep rounded-xl h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <button data-testid={`staff-reset-pw-${u.id}`} onClick={() => resetPw(u)} className="btn-link text-xs">Reimposta password</button>
            <label className="inline-flex items-center gap-2 text-xs text-warm-text">
              Attivo <Switch data-testid={`staff-active-${u.id}`} checked={u.active} disabled={u.id === user.id} onCheckedChange={(v) => patch(u.id, { active: v })} />
            </label>
          </div>
        ))}
      </div>
      <AddStaff open={open} onOpenChange={setOpen} onDone={reload} />
    </section>
  );
}
